import { Link as RouterLink, Navigate, useParams } from 'react-router';
import { makeStyles, tokens, Text, MessageBar, MessageBarBody } from '@fluentui/react-components';
import { useT } from '../i18n';
import { SiteFooter } from '../components/layout/SiteFooter';
import { SITE_INFO } from '../config/siteInfo';

const LEGAL_DOCS = ['usage', 'copyright', 'terms', 'privacy'] as const;
type LegalDoc = (typeof LEGAL_DOCS)[number];

const useStyles = makeStyles({
  page: { minHeight: '100vh', display: 'flex', flexDirection: 'column', backgroundColor: tokens.colorNeutralBackground2 },
  body: {
    minHeight: '100vh',
    width: '100%',
    maxWidth: '820px',
    margin: '0 auto',
    padding: '32px 16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    boxSizing: 'border-box',
  },
  card: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusLarge,
    padding: '24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  back: { color: tokens.colorBrandForegroundLink, textDecorationLine: 'none' },
});

/** Páginas legales públicas: /legal/usage | copyright | terms | privacy. */
export function LegalPage() {
  const s = useStyles();
  const t = useT();
  const { doc } = useParams();

  if (!doc || !(LEGAL_DOCS as readonly string[]).includes(doc)) return <Navigate to="/login" replace />;
  const content = t.legal.docs[doc as LegalDoc];

  return (
    <div className={s.page}>
      <div className={s.body}>
        <RouterLink className={s.back} to="/login">{t.legal.back}</RouterLink>
        <MessageBar intent="warning">
          <MessageBarBody>{t.legal.draftNotice}</MessageBarBody>
        </MessageBar>
        <article className={s.card}>
          <Text as="h1" size={700} weight="semibold">{content.title}</Text>
          <Text size={200}>{t.legal.updated}</Text>
          {content.sections.map((sec) => (
            <section key={sec.heading}>
              <Text as="h2" size={400} weight="semibold">{sec.heading}</Text>
              <Text as="p" block>{sec.body}</Text>
            </section>
          ))}
        </article>
        {SITE_INFO.contactEmail && (
          <Text size={200}>
            {t.legal.contactLabel}: <a href={`mailto:${SITE_INFO.contactEmail}`}>{SITE_INFO.contactEmail}</a>
          </Text>
        )}
      </div>
      <SiteFooter />
    </div>
  );
}
